import { useState, type FormEvent } from 'react';
import { Loader2, Play } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiPath, apiPost } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';
import { cn, formatAmount } from '@/lib/utils';

import { ApiErrorBanner } from './ApiErrorBanner';
import { ALL_GAP_TYPES, type GapType } from './types';

/** Mirror of the server PreviewResponse shape. */
interface PreviewResponse {
  unclassified: boolean;
  hasAffectation: boolean;
  gapType: string;
  segment?: { id: string; segmentId: number; segmentName: string };
  affectation?: { id: string; horizonLabel?: string; status: string };
  flowModel?: {
    id: string;
    code: string;
    name: string;
    horizonDays?: number;
    totalPeriods?: number;
  };
  cashflows?: Array<{
    stepIndex: number;
    periodLabel: string;
    nbDays: number;
    date?: string;
    amount: string;
    direction: 'INFLOW' | 'OUTFLOW';
  }>;
  totalAmount?: string;
}

interface PreviewRequest {
  accountType?: string;
  accountNature?: string;
  products?: string;
  accountReference?: string;
  accountingChapterId?: string;
  rateTypeId?: string;
  currencyId?: string;
  gapType: GapType;
  notional: string;
  valueDate?: string;
  direction?: 'INFLOW' | 'OUTFLOW';
}

const EMPTY: PreviewRequest = {
  accountType: '',
  accountNature: '',
  products: '',
  accountReference: '',
  accountingChapterId: '',
  rateTypeId: '',
  currencyId: '',
  gapType: 'LIQUIDITY',
  notional: '1000000000',
  valueDate: new Date().toISOString().slice(0, 10),
  direction: 'OUTFLOW',
};

/**
 * Walks: PositionInputs → SegmentationMatcher → affectation lookup
 * → FlowModelApplier → projected cashflows. Read-only on the server side.
 *
 * Useful for ALM analysts to verify their parameterization before running
 * real calculations against live data.
 */
export function PreviewPage() {
  const [form, setForm] = useState<PreviewRequest>(EMPTY);

  const run = useMutation<PreviewResponse, CubixApiError, PreviewRequest>({
    mutationFn: (body) => apiPost<PreviewResponse, PreviewRequest>(
      apiPath('alm', 'parameters', 'preview'),
      sanitize(body),
    ),
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    run.mutate(form);
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Parameterization preview</h1>
        <p className="text-sm text-muted-foreground">
          Test how a hypothetical position would be classified and amortized given the
          current Segmentations × Flow Models × Affectations.
        </p>
      </header>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Position inputs</CardTitle>
          <CardDescription>
            Leave a field blank to test wildcard matching. The first matching active segment wins.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid gap-3 md:grid-cols-3" onSubmit={handleSubmit}>
            <Field label="Account type" htmlFor="p-acctype">
              <select
                id="p-acctype"
                value={form.accountType}
                onChange={(e) => setForm({ ...form, accountType: e.target.value })}
                className={selectCls}
              >
                <option value="">— any —</option>
                <option value="BalanceSheet">BalanceSheet</option>
                <option value="OffBalance">OffBalance</option>
              </select>
            </Field>
            <Field label="Account nature" htmlFor="p-accnat">
              <select
                id="p-accnat"
                value={form.accountNature}
                onChange={(e) => setForm({ ...form, accountNature: e.target.value })}
                className={selectCls}
              >
                <option value="">— any —</option>
                <option value="Asset">Asset</option>
                <option value="Liability">Liability</option>
              </select>
            </Field>
            <Field label="Products" htmlFor="p-products">
              <Input id="p-products" placeholder="Bonds | Loans | Deposit | …"
                value={form.products}
                onChange={(e) => setForm({ ...form, products: e.target.value })}
              />
            </Field>

            <Field label="Account reference" htmlFor="p-accref">
              <Input id="p-accref" placeholder="3010"
                value={form.accountReference}
                onChange={(e) => setForm({ ...form, accountReference: e.target.value })}
              />
            </Field>
            <Field label="Rate type" htmlFor="p-rate">
              <Input id="p-rate" placeholder="IN | FLT"
                value={form.rateTypeId}
                onChange={(e) => setForm({ ...form, rateTypeId: e.target.value })}
              />
            </Field>
            <Field label="Currency" htmlFor="p-ccy">
              <Input id="p-ccy" placeholder="MAD" maxLength={3}
                value={form.currencyId}
                onChange={(e) => setForm({ ...form, currencyId: e.target.value.toUpperCase() })}
              />
            </Field>

            <Field label="Gap type" htmlFor="p-gap">
              <select id="p-gap"
                value={form.gapType}
                onChange={(e) => setForm({ ...form, gapType: e.target.value as GapType })}
                className={selectCls}
              >
                {ALL_GAP_TYPES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </Field>
            <Field label="Notional" htmlFor="p-notional">
              <Input id="p-notional" required
                value={form.notional}
                onChange={(e) => setForm({ ...form, notional: e.target.value })}
              />
            </Field>
            <Field label="Direction" htmlFor="p-dir">
              <select id="p-dir"
                value={form.direction}
                onChange={(e) => setForm({ ...form, direction: e.target.value as 'INFLOW' | 'OUTFLOW' })}
                className={selectCls}
              >
                <option value="OUTFLOW">OUTFLOW (liability)</option>
                <option value="INFLOW">INFLOW (asset)</option>
              </select>
            </Field>

            <Field label="Value date" htmlFor="p-date">
              <Input id="p-date" type="date"
                value={form.valueDate}
                onChange={(e) => setForm({ ...form, valueDate: e.target.value })}
              />
            </Field>

            <div className="md:col-span-3 space-y-2">
              {run.isError ? <ApiErrorBanner error={run.error} /> : null}
              <Button type="submit" disabled={run.isPending}>
                {run.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Play className="mr-2 h-4 w-4" />
                )}
                Run preview
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {run.data ? <PreviewResults result={run.data} /> : null}
    </div>
  );
}

function PreviewResults({ result }: { result: PreviewResponse }) {
  if (result.unclassified) {
    return (
      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="text-base text-destructive">No segment matched</CardTitle>
          <CardDescription>
            The position would land in the UNCLASSIFIED bucket. Tighten or broaden the
            criteria on one of your segments to capture it.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const noAff = !result.hasAffectation;

  return (
    <>
      <div className="grid gap-4 md:grid-cols-3">
        <Tile
          title="Matched segment"
          primary={result.segment ? `#${result.segment.segmentId} ${result.segment.segmentName}` : '—'}
          subtle="First-match-wins by priority"
        />
        <Tile
          title={`Affectation (${result.gapType})`}
          primary={
            noAff
              ? 'No active affectation'
              : `[${result.flowModel?.code}] ${result.flowModel?.name}`
          }
          subtle={
            noAff
              ? 'Add one in the Affectations page'
              : `Horizon: ${result.affectation?.horizonLabel ?? '—'} · status ${result.affectation?.status}`
          }
          tone={noAff ? 'warn' : 'ok'}
        />
        <Tile
          title="Total cashflow"
          primary={result.totalAmount ? formatAmount(result.totalAmount) : '—'}
          subtle={`${result.cashflows?.length ?? 0} step(s)`}
        />
      </div>

      {(result.cashflows?.length ?? 0) > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Projected cashflows</CardTitle>
            <CardDescription>
              Generated by applying the affectation's flow model to the position notional.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">#</th>
                    <th className="px-4 py-2 text-left font-medium">Period</th>
                    <th className="px-4 py-2 text-right font-medium">Days</th>
                    <th className="px-4 py-2 text-left font-medium">Date</th>
                    <th className="px-4 py-2 text-right font-medium">Amount</th>
                    <th className="px-4 py-2 text-left font-medium">Dir</th>
                  </tr>
                </thead>
                <tbody>
                  {result.cashflows!.map((c) => (
                    <tr key={c.stepIndex} className="border-b last:border-0">
                      <td className="px-4 py-2 text-muted-foreground">{c.stepIndex}</td>
                      <td className="px-4 py-2 font-medium">{c.periodLabel}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{c.nbDays}</td>
                      <td className="px-4 py-2 text-muted-foreground">{c.date ?? '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{formatAmount(c.amount)}</td>
                      <td className="px-4 py-2">
                        <span
                          className={cn(
                            'rounded px-1.5 py-0.5 text-[11px] font-medium',
                            c.direction === 'INFLOW'
                              ? 'bg-emerald-500/10 text-emerald-700'
                              : 'bg-amber-500/10 text-amber-700',
                          )}
                        >
                          {c.direction}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </>
  );
}

function Tile({
  title, primary, subtle, tone,
}: {
  title: string; primary: string; subtle?: string; tone?: 'ok' | 'warn';
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle className={cn('text-lg', tone === 'warn' ? 'text-destructive' : '')}>
          {primary}
        </CardTitle>
      </CardHeader>
      {subtle ? (
        <CardContent className="pt-0 text-xs text-muted-foreground">{subtle}</CardContent>
      ) : null}
    </Card>
  );
}

function sanitize(b: PreviewRequest): PreviewRequest {
  // Server treats blank strings as "not provided"; we strip them so they
  // serialize as missing rather than empty-string criteria.
  const out: PreviewRequest = { ...b };
  (Object.keys(out) as (keyof PreviewRequest)[]).forEach((k) => {
    if (typeof out[k] === 'string' && (out[k] as string).trim() === '') {
      delete out[k];
    }
  });
  return out;
}

const selectCls =
  'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

function Field({ label, htmlFor, children }: {
  label: string; htmlFor: string; children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  );
}
